import React, { PureComponent } from 'react'
import { Provider } from 'react-redux'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import * as PIXI from 'pixi.js-legacy'
import PropTypes from 'prop-types'
import { layout } from '@manychat/mudra'
import ReactPIXI from '@manychat/react-pixi'

import './pixi-object-init'
import { BuilderIdProvider } from 'common/builder/components/builder/BuilderIdProvider/BuilderIdProvider'
import config from 'common/builder/components/chart/FlowChart/config'
import { NODE_PLACEHOLDER_ID } from 'common/builder/components/chart/FlowChart/constants'
import { FlowChartStateContextProvider } from 'common/builder/components/chart/FlowChart/contexts/FlowChartStateContext'
import { ServicesProvider } from 'common/builder/components/chart/FlowChart/contexts/ServiceProvider'
import { CHOOSE_FIRST_STEP_NODE, NodeType } from 'common/builder/constants/NodeType'
import { NodeProvider } from 'common/builder/controllers/chart/FlowChart/nodes/shared/NodeContext'
import { McClientContextProvider } from 'utils/api/query/McClientContext'
import { analyticsService, flowBuilderEvents } from 'utils/services/analytics'

import app from './app'
import { STATES, MIN_DRAG_DELTA } from './config'
import Group from './shapes/Group'

const nodesToLayout = new Set()

const renderer = (root) => {
  nodesToLayout.add(root)
  app.render()
}

app.on('willRender', () => {
  for (const node of [...nodesToLayout]) {
    layout(node)
  }
  nodesToLayout.clear()
})

class PixiObj extends PureComponent {
  static propTypes = {
    node: PropTypes.object,
  }

  static noop = () => {}

  UNSAFE_componentWillMount() {
    if (this.useReactPIXIRendererInternal()) {
      this.obj = this.reactPIXICreateObject()
      this.reactPIXIRenderInternal()
      layout(this.obj.ctx)
    } else {
      this.obj = this.createObject()
    }

    if (app.isNode(this.obj)) {
      this.obj.on('move', this.handleMove)
    }

    // Calling position BEFORE mounting, so 'nodeMounted' event will be fired with correct node position
    // TODO: should we fire 'nodeMounted' from autoPositionNode method?
    this.autoPositionNode()

    const $obj = this.obj.ctx || this.obj
    if (this.obj.name === 'connector' && !this.props.draft) {
      app.root.stage.addChildAt($obj, 0)
    } else {
      app.root.stage.addChild($obj)
    }

    this.onMount()
  }

  componentWillUnmount() {
    this.saveCoordinates.cancel()

    this.onUnmount()

    if (this.useReactPIXIRendererInternal()) {
      this.reactPIXIRenderInternal(null)
    }

    this.obj.destroy({ children: true })
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(this.props, prevProps) || !isEqual(this.state, prevState)) {
      if (this.useReactPIXIRendererInternal()) {
        this.reactPIXIRenderInternal()
      } else {
        this.rerender(prevProps, prevState)
      }
      this.onUpdate(prevProps, prevState)
    }
  }

  handleMove = () => {
    this.saveCoordinates()
  }

  handleNewConnection = () => {
    const { onNewConnection, node } = this.props
    if (this.isConnectionAllowed()) {
      onNewConnection(app.stateOptions.src, node.id)
    }
  }

  saveCoordinates = debounce(() => {
    const { obj } = this

    // Not saving coordinates while el or some of parents is dragging
    let el = obj.ctx,
      dragging = false
    while (el != null) {
      if (el.dragging) {
        dragging = true
        break
      }
      el = el.parent
    }
    if (dragging) {
      return this.saveCoordinates()
    }

    const { saveCoordinates = () => {} } = this.props

    const { x, y } = obj.getAbsolutePosition()

    const current = this.props.coordinates[obj.id]
    if (current != null && x === current.x && y === current.y) {
      return
    }

    // TODO: save parent id when object in group
    saveCoordinates(obj.id, x, y)

    analyticsService.logFlowBuilderEvent(flowBuilderEvents.DRAG_AND_DROP_NODE, {
      node: this.props.node,
    })
  }, 300)

  createObject() {
    return null
  }
  rerender() {}
  onMount() {}
  onUpdate() {}
  onUnmount() {}

  autoPositionNode() {
    const { obj } = this

    if (!app.isNode(obj)) {
      return
    }

    const { coordinates = {} } = this.props
    const isChooseFirstStepNode = this.props.node?.id === CHOOSE_FIRST_STEP_NODE
    if (isChooseFirstStepNode) {
      const startingStepCoordinates = coordinates[NodeType.STARTING_STEP]
      const x = startingStepCoordinates?.x ?? 0
      const y = startingStepCoordinates?.y ?? 0
      this.obj.position.set(
        x + config.STARTING_STEP_NODE_WIDTH + config.CONTENT_POSITION_RIGHT_MARGIN,
        y,
        true,
      )
    } else if (coordinates[obj.id]) {
      const { x, y } = coordinates[obj.id]
      this.obj.position.set(x, y, true)
    }
  }

  render() {
    return null
  }

  //----------------------New Rendering API--------------------

  reactPIXIHandleSelectSegment = (segmentId) => {
    const { onSelectSegment } = this.props
    if (segmentId == null || typeof onSelectSegment !== 'function') {
      return
    }

    // Preventing modal closing by opening it after event
    setTimeout(() => {
      onSelectSegment(segmentId)
    })
  }

  reactPIXICreateObject() {
    const { node, readonly, rootId, disableFlowChartInteractions } = this.props
    const $g = new Group({
      id: node.id,
      onboardingElementId: node.onboardingElementId,
      name: `node/${node.nodeType}`,
      interactive: true,
    })
    $g.ctx.testId = node.id === rootId ? 'start' : node.id

    $g.getConnectorsStart = () => {
      // Temporary realization for October 2019 release
      const [node] = $g.getChildren()
      return {
        x: this.reactPIXIGetWidth(),
        y: node.layout.height - 38,
      }
    }

    $g.getLocalWidth = () => {
      const [nodeCard] = $g.ctx.children
      if (nodeCard && nodeCard.style && nodeCard.style.width) {
        return nodeCard.style.width.value
      }
      return 0
    }

    $g.getMenuPosition = (point) => {
      const width = $g.getLocalWidth()
      const origin = new PIXI.Point(width / 2, 0)
      return $g.ctx.toGlobal(origin, point)
    }

    const handleClick = (e) => {
      if (this.props.disableFlowChartInteractions) {
        return
      }
      if (app.state === STATES.CREATING_NEW_CONNECTOR && app.stateOptions.byClick && !readonly) {
        return this.handleNewConnection()
      }
      if (app.isBlock(e.target) && typeof this.props.onBlockClick === 'function') {
        if (readonly) {
          this.props.onClick(e, node.id)
        }
        this.props.onBlockClick(e, e.target.name.split('/')[1], node.id)
      }
      if (app.isButton(e.target) && typeof this.props.onButtonClick === 'function') {
        analyticsService.logFlowBuilderEvent(flowBuilderEvents.SELECT_BUTTON, {
          node,
          button: e.target,
        })
        this.props.onButtonClick(e, e.target.name.split('/')[1], node.id)
      }
      if (
        e.target !== e.currentTarget &&
        ![NODE_PLACEHOLDER_ID, 'nodecard', 'comment-node'].includes(e.target.name)
      ) {
        return
      }

      if (e.target.name === NODE_PLACEHOLDER_ID) {
        analyticsService.logFlowBuilderEvent(flowBuilderEvents.CLICK_NODE_PLACEHOLDER, { node })
      }

      this.props.onClick(e, node.id)
    }

    const handleMouseUp = (e) => {
      if (app.state === STATES.CREATING_NEW_CONNECTOR && !app.stateOptions.byClick) {
        return this.handleNewConnection()
      }
    }

    $g.on('touchend', (e) => {
      if (e.data.multitouch) {
        return
      }

      if (app.state !== STATES.NORMAL) {
        return handleMouseUp(e)
      }

      if (e.data.mouseDragDelta > MIN_DRAG_DELTA) {
        return
      }
      if (app.getHoveredNode() !== $g) {
        app.setHover($g)
        return
      }
      handleClick(e)
    })

    $g.on('click', handleClick)

    $g.ctx.getCullingBounds = (rect) => {
      const card = $g.ctx.children[0]
      if (!card) {
        rect.x = 0
        rect.y = 0
        rect.width = 0
        rect.height = 0
        return
      }

      const position = $g.getAbsolutePosition()
      const { boundingRect } = card.layout
      rect.x = position.x + boundingRect.x
      rect.y = position.y + boundingRect.y
      rect.width = boundingRect.width
      rect.height = boundingRect.height
      return rect
    }

    if (readonly || disableFlowChartInteractions) {
      return $g
    }

    $g.on('mouseup', handleMouseUp)

    $g.draggable({
      onDragStart: (e) => {
        if ($g.ctx.parent.id === '$SelectedGroup$') {
          return false
        }
      },
      onDragEnd: (e, draggedElement) => {
        if (draggedElement !== $g) {
          const { x, y } = draggedElement.position
          this.props.cloneNodes([node.id], { x, y })
        }
      },
      validateTarget: (e) => {
        if (!this.isDraggable()) {
          return false
        }
        if (
          [NODE_PLACEHOLDER_ID, 'nodecard', 'comment-node'].includes(e.target.name) ||
          app.isBlock(e.target) ||
          app.isButton(e.target)
        ) {
          return true
        }
        if (e.target !== e.currentTarget) {
          return false
        }
        return true
      },
      dragAvatarWithAlt: true,
    })

    return $g
  }

  reactPIXIRender() {
    // see realisation in nodes
  }

  reactPIXIRenderInternal(children = this.reactPIXIRender()) {
    const root = this.obj
    const {
      builderId,
      node,
      noStat,
      readonly,
      disableFlowChartInteractions,
      flowChartState,
      onNewConnectorDragStart,
      onNewConnectorDragEnd,
      onAnswersBtnClick,
      rootId,
      services,
    } = this.props

    const nodeContext = {
      builderId,
      rootId,
      node,
      noStat,
      readonly,
      handlers: {
        onNewConnectorDragStart,
        onNewConnectorDragEnd,
        onSelectSegment: this.reactPIXIHandleSelectSegment,
      },
      disableFlowChartInteractions,
      onAnswersBtnClick,
    }

    /**
     * https://pixijs.io/pixi-react/context-bridge/
     * Pixi-react does not support propagation of parent contexts.
     * That's why we need to create a bridges to get access to contexts inside pixi components
     * Need to create a mechanism for a dynamic context provider (TODO: AI-373)
     */
    const content =
      children != null ? (
        <Provider store={window.app.store}>
          <McClientContextProvider api={window.app.mcClient.api} cache={window.app.mcClient.cache}>
            <BuilderIdProvider builderId={builderId}>
              <NodeProvider builderId={builderId} value={nodeContext}>
                <ServicesProvider value={services}>
                  <FlowChartStateContextProvider value={flowChartState}>
                    {children}
                  </FlowChartStateContextProvider>
                </ServicesProvider>
              </NodeProvider>
            </BuilderIdProvider>
          </McClientContextProvider>
        </Provider>
      ) : null

    ReactPIXI.render(content, root.ctx, {
      renderer,
    })
  }

  useReactPIXIRenderer() {
    return false
  }

  useReactPIXIRendererInternal() {
    return this.useReactPIXIRenderer()
  }

  reactPIXIGetWidth() {
    // see other realisations in nodes
    return 440
  }

  isConnectionAllowed = () => {
    if (app.state !== STATES.CREATING_NEW_CONNECTOR) {
      return true
    }
    const { node, isConnectionAllowed } = this.props
    const srcId = app.stateOptions && app.stateOptions.src
    return isConnectionAllowed(srcId, node.id)
  }

  isTargetAllowed = () => {
    const { isTargetAllowed, node } = this.props
    return isTargetAllowed(node.id)
  }

  isDraggable = () => {
    const { node, isChooseFirstStepNodeVisible } = this.props
    if (node?.id === CHOOSE_FIRST_STEP_NODE) {
      return false
    }
    if (node?.nodeType === NodeType.STARTING_STEP && isChooseFirstStepNodeVisible) {
      return false
    }
    return true
  }
}

export default PixiObj
